"use client"

import type React from "react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"

interface ContactFormProps {
  formTitle: string
  successHeading: string
  successBody: string
  ageConfirmation: string
}

export default function ContactForm({
  formTitle,
  successHeading,
  successBody,
  ageConfirmation,
}: ContactFormProps) {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    await new Promise((resolve) => setTimeout(resolve, 1500))
    console.log("Form submitted:", formState)
    setIsSubmitting(false)
    setIsSubmitted(true)
    setFormState({ name: "", email: "", phone: "", message: "" })
  }

  if (isSubmitted) {
    return (
      <div className="bg-white shadow-lg p-8 rounded-xl border border-gray-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 left-0 h-2 bg-gradient-to-r from-green-500 to-primary_green"></div>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-600 mb-6 shadow-inner ring-4 ring-green-50 animate-appearance-in">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-3xl font-semibold text-gray-800 mb-3">
            {successHeading}
          </h3>
          <p className="text-gray-600 max-w-md mx-auto mb-8">{successBody}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => setIsSubmitted(false)}
              className="bg-white border border-green-600 hover:bg-green-50 text-green-600 px-6"
              variant="outline"
            >
              Send Another Message
            </Button>
            <Button
              onClick={() => (window.location.href = "/shop")}
              className="bg-primary_green hover:bg-primary_green/90 text-white px-6"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
        <style jsx>{`
          @keyframes appearance-in {
            0% {
              transform: scale(0.8);
              opacity: 0;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          .animate-appearance-in {
            animation: appearance-in 0.5s ease-out forwards;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-6 md:p-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">{formTitle}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="name" className="text-gray-700">
            Full Name
          </Label>
          <Input
            id="name"
            name="name"
            value={formState.name}
            onChange={handleChange}
            required
            className="mt-1 bg-gray-50 border-gray-200 focus:border-green-500 focus:ring-green-500"
            placeholder="John Smith"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-gray-700">
            Email Address
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formState.email}
            onChange={handleChange}
            required
            className="mt-1 bg-gray-50 border-gray-200 focus:border-green-500 focus:ring-green-500"
            placeholder="john@example.com"
          />
        </div>
        <div>
          <Label htmlFor="phone" className="text-gray-700">
            Phone Number (optional)
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formState.phone}
            onChange={handleChange}
            className="mt-1 bg-gray-50 border-gray-200 focus:border-green-500 focus:ring-green-500"
            placeholder="(716) 123-4567"
          />
        </div>
        <div>
          <Label htmlFor="message" className="text-gray-700">
            Your Message
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formState.message}
            onChange={handleChange}
            required
            className="mt-1 h-32 bg-gray-50 border-gray-200 focus:border-green-500 focus:ring-green-500"
            placeholder="How can we help you today?"
          />
        </div>
        <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600 border border-gray-100">
          <p>{ageConfirmation}</p>
        </div>
        <Button
          type="submit"
          className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 transition-colors"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Sending...
            </span>
          ) : (
            "Send Message"
          )}
        </Button>
      </form>
    </div>
  )
}
